<template>
  <Modal
    :modal-types="[DELETE_CHART_STYLE_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-trash-alt"
    modal-title="Delete ChartStyle"
  >
    <form
      class="delete-form"
      @submit.prevent="handleDeleteChartStyleFormSubmit()"
    >
      <p v-show="!isAttemptingToDeleteTheDefaultChartStyle" class="delete-chart-style-message">
        Are you sure you'd like to delete your saved ChartStyle?
        <br />
        <br />
        <span class="delete-chart-style-message-saved-chart-style-name" v-sanitize-html="nameOfChartStyleToBeDeleted"></span>
        <br />
        <br />
        <span class="delete-chart-style-permanent-message">This action cannot be&nbsp;undone.</span>
      </p>
      <p v-show="isAttemptingToDeleteTheDefaultChartStyle" class="delete-chart-style-message">
        You cannot delete your default ChartStyle
        <br />
        <br />
        <span class="delete-chart-style-message-saved-chart-style-name" v-sanitize-html="nameOfChartStyleToBeDeleted"></span>
        <br />
      </p>
      <button
        v-show="!isAttemptingToDeleteTheDefaultChartStyle"
        type="submit"
        class="sc3-button sc3-button-red"
        :disabled="isAttemptingToDeleteTheDefaultChartStyle || isDeletingChartStyle"
      >
        <i v-show="!isDeletingChartStyle" class="fas fa-fw fa-trash-alt"></i>
        <i v-show="isDeletingChartStyle" class="fad fa-fw fa-spinner-third fa-spin"></i>
        &nbsp;Delete Saved ChartStyle
      </button>
      <button
        v-show="isAttemptingToDeleteTheDefaultChartStyle"
        type="button"
        class="sc3-button sc3-button-green"
        @click="closeModal()"
      >
        <i class="fas fa-fw fa-check-circle"></i>
        &nbsp;OK!
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Cancel
      </button>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useModals } from '@/composables/useModals';
  import { deleteChartStyle } from '@/composables/deleteChartStyle';
  import { INVALID_CHART_STYLE_RECID, isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
  import { refreshChartStyles } from '@/composables/refreshChartStyles';
  import { refreshDefaultChartStyle } from '@/composables/refreshDefaultChartStyle';
  import { getChartStyle } from '@/composables/getChartStyle';
  import { areObjectsEqual } from '@/utils/areObjectsEqual';
  import Modal from '@/components/modals/Modal.vue';

  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { modalType } = storeToRefs(layoutStore);
  const { recidOfChartStyleBeingDeletedInModalView } = storeToRefs(savedChartsStore);
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { DELETE_CHART_STYLE_MODAL, closeModal } = useModals();
  const isDeletingChartStyle = ref(false);
  let lastModalOpenedTime = -1;
  let lastRecidOfChartStyleBeingDeletedInModalViewChangedTime = -1;
  const chartStyleToBeDeleted = computed(() => {
    if (!isValidChartStyleRecid(recidOfChartStyleBeingDeletedInModalView.value, false)) {
      return null;
    }

    return savedChartsStore.chartStyles.find(cs => cs.recid === recidOfChartStyleBeingDeletedInModalView.value) ?? null;
  });
  const nameOfChartStyleToBeDeleted = computed(() => {
    return chartStyleToBeDeleted.value ? chartStyleToBeDeleted.value.name : '';
  });
  const isAttemptingToDeleteTheDefaultChartStyle = computed(() => {
    if (!isValidChartStyleRecid(recidOfChartStyleBeingDeletedInModalView.value, false)) {
      return false;
    }

    return savedChartsStore.recidOfDefaultChartStyle === recidOfChartStyleBeingDeletedInModalView.value;
  });

  interface DeleteChartStyleModalState {
    modalType: string;
    lastModalOpenedTime: number;
    recidOfChartStyleBeingDeletedInModalView: number;
    lastRecidOfChartStyleBeingDeletedInModalViewChangedTime: number;
  };

  async function handleDeleteChartStyleFormSubmit() {
    if (
      isDeletingChartStyle.value ||
      isAttemptingToDeleteTheDefaultChartStyle.value ||
      modalType.value !== DELETE_CHART_STYLE_MODAL
    ) {
      return;
    }

    isDeletingChartStyle.value = true;
    const modalState = getModalState();
    const chartStyleRecid = recidOfChartStyleBeingDeletedInModalView.value;

    if (!isValidChartStyleRecid(chartStyleRecid, false)) {
      window.sc3WorkbenchLogger.error(`Unable to delete ChartStyle from DeleteChartStyleModal - chartStyleRecid is invalid: ${chartStyleRecid}.`);
      closeModal();
      addRedActionResultMessage('Failed to delete ChartStyle.');
      return;
    }

    const chartStyle = await getChartStyle(chartStyleRecid, true);

    if (hasModalStateChanged(modalState)) {
      return;
    }

    if (!chartStyle) {
      window.sc3WorkbenchLogger.error(`Unable to delete ChartStyle from DeleteChartStyleModal - no ChartStyle found with recid: ${chartStyleRecid}.`);
      closeModal();
      addRedActionResultMessage('Failed to delete ChartStyle.');
      return;
    }

    try {
      await deleteChartStyle(chartStyleRecid.toString());

      savedChartsStore.$patch((state) => {
        state.chartStyles = state.chartStyles.filter(cs => cs.recid !== chartStyleRecid);

        if (state.recidOfDefaultChartStyle === chartStyleRecid) {
          state.recidOfDefaultChartStyle = INVALID_CHART_STYLE_RECID;
        }

        if (state.recidOfChartStyleBeingPreviewedInChartLibraryListView === chartStyleRecid) {
          state.recidOfChartStyleBeingPreviewedInChartLibraryListView = INVALID_CHART_STYLE_RECID;
        }

        state.recidOfChartStyleBeingDeletedInModalView = INVALID_CHART_STYLE_RECID;
        state.recidOfChartStyleBeingEditedInModalView = INVALID_CHART_STYLE_RECID;
      });

      if (chartStore.recidOfChartStyleBeingViewed === chartStyleRecid) {
        chartStore.$patch({ recidOfChartStyleBeingViewed: INVALID_CHART_STYLE_RECID });
      }

      addGreenActionResultMessage('Success! Your ChartStyle has been deleted!');
      closeModal();

      return;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Error occurred while deleting ChartStyle with recid: ${chartStyleRecid}.`, e);
    }

    if (hasModalStateChanged(modalState)) {
      return;
    }

    addRedActionResultMessage('Failed to delete ChartStyle.');
    closeModal();

    await Promise.all([
      refreshChartStyles(),
      refreshDefaultChartStyle()
    ]);
  }

  function getModalState(): DeleteChartStyleModalState {
    return {
      modalType: modalType.value,
      lastModalOpenedTime,
      recidOfChartStyleBeingDeletedInModalView: recidOfChartStyleBeingDeletedInModalView.value,
      lastRecidOfChartStyleBeingDeletedInModalViewChangedTime
    };
  }

  function hasModalStateChanged(modalState: DeleteChartStyleModalState) {
    const currentModalState = getModalState();

    return !areObjectsEqual(modalState, currentModalState);
  }

  async function handleDeleteChartStyleModalOpened() {
    await Promise.all([
      getChartStyle(recidOfChartStyleBeingDeletedInModalView.value, true),
      refreshDefaultChartStyle()
    ]);
  }

  function handleDeleteChartStyleModalClosed(isReInitializingModal: boolean) {
    isDeletingChartStyle.value = false;

    if (!isReInitializingModal) {
      savedChartsStore.$patch({ recidOfChartStyleBeingDeletedInModalView: INVALID_CHART_STYLE_RECID });
    }
  }

  async function handleRecidOfChartStyleBeingDeletedInModalViewChanged() {
    lastRecidOfChartStyleBeingDeletedInModalViewChangedTime = Date.now();

    handleDeleteChartStyleModalClosed(true);

    if (modalType.value !== DELETE_CHART_STYLE_MODAL) {
      return;
    }

    await handleDeleteChartStyleModalOpened();
  }

  async function handleModalTypeChanged() {
    if (modalType.value !== DELETE_CHART_STYLE_MODAL) {
      handleDeleteChartStyleModalClosed(false);
      return;
    }

    lastModalOpenedTime = Date.now();

    await handleDeleteChartStyleModalOpened();
  }

  watch(recidOfChartStyleBeingDeletedInModalView, handleRecidOfChartStyleBeingDeletedInModalViewChanged);
  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .delete-form {
    margin: 0px;
    padding: 0px;
    text-align: center;
  }

  .delete-chart-style-message {
    margin: 0px 0px 15px 0px;
    padding: 0px;
    font-size: 16px;
    font-weight: 500;
    color: #212529;
    line-height: 1.6;
  }

  .delete-chart-style-message-saved-chart-style-name {
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
  }

  .delete-chart-style-permanent-message {
    font-weight: 700;
  }
</style>
